import React, { useEffect, useState } from 'react'
import { Flex, Tooltip, Text, TextVariant, Button, ButtonVariant, HistoryIcon, useModal } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Info } from 'react-feather'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import useScreenSize from 'hooks/useScreenSize'
import { useGetMproPrice } from 'hooks/useGetMproPrice'
import isNil from 'lodash/isNil'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { BNToNumberWithDecimals, getNativeCoinTickerByChainId, MprolabContractName } from '@mprolab/sdk'
import { ClaimRewardButton, ColumnComponent, StakeButton, TableContent } from 'views/Stake/components'
import { formatPrice, useStakePanel } from 'views/Stake/stake.logic'
import { StakeHistory } from 'views/Stake/widgets'

dayjs.extend(duration)

const BorderContainerContrast = styled(Flex)`
  padding: 8px 16px;
  border-radius: 8px;
  min-height: 64px;
  background: ${({ theme }) => theme.colors.gray11};
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column;
    gap: 16px;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 8px 12px;
  }
`

const SingleAutostake: React.FC<{
  address: string
  chainId: number
  name: string
}> = ({ address, chainId: stakeChainId, name }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { isMobile } = useScreenSize()
  const { account } = useWeb3React()
  const { config, isLoading, claimStake, claimReward, fetchAllData } = useStakePanel(address)
  const { getMproPrice, mproPriceLoading } = useGetMproPrice()
  const { stakeConfig, claimConfig, rewardConfig } = config
  const [onPresentStakeHistoryModal] = useModal(<StakeHistory strakeContractAddress={address} />)
  const isDual = name === MprolabContractName.MPRO_DUAL_AUTO_STAKE

  const NATIVE_TICKER = getNativeCoinTickerByChainId(stakeChainId)
  const TICKER = 'MPRO'

  const [stakedAmountUSD, setStakedAmountUSD] = useState<number>()
  const [earnedAmountUSD, setEarnedAmountUSD] = useState<number>()
  const loadingData = isLoading || mproPriceLoading

  useEffect(() => {
    const fetchPrices = async () => {
      const stakedPrice = await getMproPrice(stakeConfig?.stakedAmount, stakeChainId)
      const earnedPrice = await getMproPrice(stakeConfig?.earnedAmount, stakeChainId)
      setStakedAmountUSD(stakedPrice)
      setEarnedAmountUSD(earnedPrice)
    }

    fetchPrices()
  }, [stakeConfig?.stakedAmount, stakeConfig?.earnedAmount, account, mproPriceLoading])

  const StakeHistoryButton = () => {
    return (
      <Flex fullWidth={isMobile}>
        <Button
          fullWidth={isMobile}
          size={isMobile ? 'md' : 'sm'}
          onClick={onPresentStakeHistoryModal}
          variant={ButtonVariant.TEXT}
          startIcon={<HistoryIcon />}
        >
          {t('stakingPanel.stakingButton')}
        </Button>
      </Flex>
    )
  }

  const ContentView = () => {
    return (
      <>
        <BorderContainerContrast mt="16px">
          {/* Total staked amount */}
          <ColumnComponent
            title={t('stakingPanel.totalStaked')}
            value={`${formatPrice(BNToNumberWithDecimals(stakeConfig?.totalStakedAmount, 18, 6))} MPRO`}
            {...{ isLoading }}
          />
          <Flex
            gap={isMobile ? '16px' : '32px'}
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'initial' : 'center'}
          >
            {/* Staked  amount */}
            <ColumnComponent
              title={t('stakingPanel.personalStaked')}
              value={`${formatPrice(BNToNumberWithDecimals(stakeConfig?.stakedAmount, 18, 6))} ${TICKER}`}
              subValue={isNil(stakedAmountUSD) ? '~ $ 0' : `~ $ ${formatPrice(stakedAmountUSD)}`}
              rightAligned={!isMobile}
              isLoading={loadingData}
            />
            {/* Earned amount */}
            <ColumnComponent
              title={t('stakingPanel.earned')}
              value={`${formatPrice(BNToNumberWithDecimals(stakeConfig?.earnedAmount, 18, 6))} ${TICKER}`}
              subValue={isNil(earnedAmountUSD) ? '~ $ 0' : `~ $ ${formatPrice(earnedAmountUSD)}`}
              rightAligned={!isMobile}
              isLoading={loadingData}
            />
            {!isMobile && account && <StakeHistoryButton />}
          </Flex>
          {isMobile && account && <StakeHistoryButton />}
        </BorderContainerContrast>
        {account && (
          <>
            <BorderContainerContrast mt="16px">
              <Flex gap={isMobile ? '16px' : '48px'} flexDirection={isMobile ? 'column' : 'row'}>
                {/* Currency */}
                <ColumnComponent title={t('stakingPanel.currency')} value={TICKER} isLoading={loadingData} />
                {/* Total to claim Value */}
                <ColumnComponent
                  title={t('stakingPanel.totalToClaim')}
                  value={`${formatPrice(BNToNumberWithDecimals(claimConfig?.totalToClaim, 18, 6))} ${TICKER}`}
                  isLoading={loadingData}
                />
                {/* Next to claim Value */}
                <ColumnComponent
                  title={t('stakingPanel.nextToClaim')}
                  value={`${formatPrice(BNToNumberWithDecimals(claimConfig?.nextToClaim, 18, 6))} ${TICKER}`}
                  isLoading={loadingData}
                />
                {/* Current to claim Value */}
                <ColumnComponent
                  title={t('stakingPanel.currentToClaim')}
                  value={`${formatPrice(BNToNumberWithDecimals(claimConfig?.currentToClaim, 18, 6))} ${TICKER}`}
                  isLoading={loadingData}
                />
              </Flex>
              <Flex gap="16px" alignItems="center" fullWidth={isMobile}>
                <StakeButton
                  currentToClaim={claimConfig?.currentToClaim}
                  nextToClaim={claimConfig?.nextToClaim}
                  totalToClaim={claimConfig?.totalToClaim}
                  nextReleaseTimestamp={claimConfig?.nextReleaseTimestamp?.toNumber()}
                  isLoading={loadingData}
                  targetChainId={stakeChainId}
                  {...{ claimStake, fetchAllData }}
                />
                {!isMobile && (
                  <Flex style={{ cursor: 'pointer' }}>
                    <Tooltip
                      customPadding="16px"
                      minWidth="323px"
                      title={
                        <Flex alignItems="center" gap="4px" minWidth="400px">
                          <Text variant={TextVariant.BODY_SMALL} style={{ whiteSpace: 'nowrap' }}>
                            {t('stakingPanel.claimTooltipDescrption')}
                          </Text>
                        </Flex>
                      }
                    >
                      <Info color={colors.gray6} size={18} />
                    </Tooltip>
                  </Flex>
                )}
              </Flex>
            </BorderContainerContrast>
            {isDual && (
              <BorderContainerContrast mt="16px">
                <Flex gap={isMobile ? '16px' : '48px'} flexDirection={isMobile ? 'column' : 'row'}>
                  {/* Total to claim Value */}
                  <ColumnComponent title={t('stakingPanel.currency')} value={NATIVE_TICKER} isLoading={loadingData} />
                  {/* Current to claim Value */}
                  <ColumnComponent
                    title={t('stakingPanel.currentToClaim')}
                    value={`${formatPrice(
                      BNToNumberWithDecimals(rewardConfig?.currentToClaim, 18, 18),
                      18
                    )} ${NATIVE_TICKER}`}
                    isLoading={loadingData}
                  />
                </Flex>
                <Flex gap="16px" alignItems="center" fullWidth={isMobile}>
                  <ClaimRewardButton
                    {...{ fetchAllData }}
                    totalToClaim={rewardConfig?.currentToClaim}
                    isLoading={loadingData}
                    claim={claimReward}
                    targetChainId={stakeChainId}
                  />
                  {!isMobile && (
                    <Flex style={{ cursor: 'pointer' }}>
                      <Tooltip
                        customPadding="16px"
                        minWidth="323px"
                        title={
                          <Flex alignItems="center" gap="4px" minWidth="400px">
                            <Text variant={TextVariant.BODY_SMALL} style={{ whiteSpace: 'nowrap' }}>
                              {t('stakingPanel.claimRewardTooltipDescrption')}
                            </Text>
                          </Flex>
                        }
                      >
                        <Info color={colors.gray6} size={18} />
                      </Tooltip>
                    </Flex>
                  )}
                </Flex>
              </BorderContainerContrast>
            )}
          </>
        )}
      </>
    )
  }

  return (
    <Flex flexDirection="column">
      <TableContent
        {...{ apy: stakeConfig?.apy, fee: stakeConfig?.fee, isLoading: loadingData, isDual }}
        chainId={stakeChainId}
      />
      <ContentView />
    </Flex>
  )
}

export default SingleAutostake
